import React, { useState, useEffect } from 'react';
import { QuestionCard } from '../QuestionCard';
import { RushTimer } from '../RushTimer';
import { BattlePlayerList } from './BattlePlayerList';
import { KeyboardHints } from '../KeyboardHints';
import { useLayoutVariant } from '../../../hooks/useLayoutVariant';
import { useRushSounds } from '../../../hooks/useRushSounds';
import type { BattleGameState } from '../../../types/battleRush';

interface BattleGameProps {
  state: BattleGameState;
  currentUserId: string;
  myAnswer: boolean;
  onAnswer: (choice: 'A' | 'B') => void;
}

export const BattleGame: React.FC<BattleGameProps> = ({
  state,
  currentUserId,
  myAnswer,
  onAnswer,
}) => {
  const layout = useLayoutVariant();
  const isDesktop = layout.type === 'desktop';
  const [selectedSide, setSelectedSide] = useState<'A' | 'B' | null>(null);

  const room = state.room;
  const roundStatus = room?.roundStatus;
  const isAnswering = roundStatus === 'answering';
  const isRevealing = roundStatus === 'revealing' && room?.roundReveal !== null;
  const currentQuestionIndex = room?.currentQuestionIndex;

  // Find my result early for sounds (before early return)
  const myResultForSound = room?.roundReveal?.playerResults.find((r) => r.userId === currentUserId);

  useRushSounds({
    timeRemaining: state.timeRemaining,
    isTimerActive: isAnswering && !myAnswer,
    answerCorrect: isRevealing && myResultForSound ? myResultForSound.correct : null,
  });

  // Reset selected side when new round starts (must be before early return)
  React.useEffect(() => {
    if (isAnswering) setSelectedSide(null);
  }, [isAnswering, currentQuestionIndex]);

  // Keyboard navigation (arrow keys + Enter)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!room || myAnswer || room.roundStatus !== 'answering') return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setSelectedSide('A');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setSelectedSide('B');
      } else if (e.key === 'Enter') {
        e.preventDefault();
        setSelectedSide(prev => {
          if (prev) onAnswer(prev);
          return prev;
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [room, myAnswer, onAnswer]);

  if (!room) return null;

  const question = room.currentQuestion;
  const reveal = room.roundReveal;
  const disabled = myAnswer || isRevealing;

  // Find my result in the reveal
  const myResult = reveal?.playerResults.find((r) => r.userId === currentUserId);

  // Determine reveal states for cards
  const getRevealState = (side: 'A' | 'B'): 'correct' | 'wrong' | null => {
    if (!isRevealing || !reveal) return null;
    return side === reveal.correctAnswer ? 'correct' : 'wrong';
  };

  const getRevealValue = (side: 'A' | 'B'): string | null => {
    if (!isRevealing || !reveal) return null;
    return side === 'A' ? reveal.formattedValueA : reveal.formattedValueB;
  };

  const handleSelect = (side: 'A' | 'B') => {
    if (disabled) return;
    setSelectedSide(side);
    onAnswer(side);
  };

  return (
    <div className={`flex ${isDesktop ? 'gap-4' : 'flex-col'} w-full max-w-5xl mx-auto px-4 pt-16 pb-4`}>
      {/* Main game area */}
      <div className={`flex-1 flex flex-col items-center ${isDesktop ? '' : 'mb-4'}`}>
        {/* Score + Question index */}
        <div className="flex items-center justify-between w-full max-w-md mb-3">
          <span className="text-xs uppercase tracking-wider" style={{ color: 'var(--rush-text-muted)' }}>
            Question {room.currentQuestionIndex + 1}/{room.totalQuestions}
          </span>
        </div>

        {/* Timer */}
        {isAnswering && (
          <div className="w-full max-w-md mb-4">
            <RushTimer
              timeRemaining={state.timeRemaining}
              timerDuration={state.timerDuration}
            />
          </div>
        )}

        {/* Question */}
        {question && (
          <>
            <p
              className="text-base sm:text-lg font-medium mb-4 text-center"
              style={{ color: 'var(--rush-text-primary)' }}
            >
              {question.questionText}
            </p>

            <div className="grid grid-cols-2 gap-3 sm:gap-4 w-full max-w-md">
              <QuestionCard
                company={question.stockA}
                label="A"
                onSelect={() => handleSelect('A')}
                disabled={disabled}
                revealState={getRevealState('A')}
                revealValue={getRevealValue('A')}
                selected={selectedSide === 'A'}
              />
              <QuestionCard
                company={question.stockB}
                label="B"
                onSelect={() => handleSelect('B')}
                disabled={disabled}
                revealState={getRevealState('B')}
                revealValue={getRevealValue('B')}
                selected={selectedSide === 'B'}
              />
            </div>

            {isDesktop && <KeyboardHints />}
          </>
        )}

        {/* Waiting indicator */}
        {myAnswer && isAnswering && (
          <p
            className="text-sm mt-4 animate-pulse"
            style={{ color: 'var(--rush-text-muted)' }}
          >
            Waiting for other players...
          </p>
        )}

        {/* Reveal result text */}
        {isRevealing && myResult && (
          <p
            className="text-lg font-bold mt-4"
            style={{ color: myResult.correct ? 'var(--rush-correct)' : 'var(--rush-wrong)' }}
          >
            {myResult.correct ? 'Correct!' : myResult.choice === 'timeout' ? 'Time\'s up!' : 'Wrong!'}
            {!myResult.correct && myResult.livesRemaining > 0 && ` (${myResult.livesRemaining} lives left)`}
            {myResult.eliminated && ' - Eliminated!'}
          </p>
        )}
      </div>

      {/* Player sidebar (desktop) / bottom strip (mobile) */}
      <div
        className={`${isDesktop ? 'w-64 flex-shrink-0' : 'w-full max-w-md mx-auto'}`}
        style={isDesktop ? { paddingTop: '2rem' } : {}}
      >
        <p
          className="text-xs uppercase tracking-wider mb-2"
          style={{ color: 'var(--rush-text-muted)' }}
        >
          Players
        </p>
        <BattlePlayerList
          players={room.players}
          currentUserId={currentUserId}
          showAnswered={isAnswering}
          compact={!isDesktop}
        />
      </div>
    </div>
  );
};
